'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import type React from 'react'
import {
	createContext,
	type Dispatch,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import type { StorageInstance } from 'storage/domain/Storage'
import useSWR from 'swr'
import { baseSession, SESSION_DIFF_TIMESTAMP } from '../constants'
import { authInfoOrchestratorService } from '../services/authInfoOrchestratorService'
import type { Session } from '../types/session'
import { hasTimestampExpired } from '../utils/cacheStorage/hasTimestampExpired'
import { setSessionCacheStorage } from '../utils/cacheStorage/setSessionCacheStorage'
import { errorRetryHandler } from '../utils/errorRetryHandler'
import { getSessionCache } from '../utils/getSessionCache'
import { sessionCache } from '../utils/sessionCache'

export const SessionContext = createContext<Session | null>(null)
export const SetSessionContext = createContext<SessionActions | null>(null)
export const SessionCacheContext = createContext<StorageInstance | null>(null)

export interface SessionActions {
	setSession: Dispatch<Session>
	handleSession: () => Promise<void>
	refreshSession: () => Promise<void>
}

type SessionProviderProps = {
	readonly children: React.ReactNode
}

export function SessionProvider({ children }: SessionProviderProps): ReactNode {
	const [session, setSession] = useState(baseSession)
	const masterData = useMasterData()
	const hasLoadedRef = useRef(false)
	const timestampRef = useRef<null | number>(null)
	const { mutate: getOrchestratorData } = useSWR(
		'auth-orchestrator',
		authInfoOrchestratorService,
		{
			revalidateOnMount: false,
			revalidateOnFocus: true,
			revalidateIfStale: false,
			revalidateOnReconnect: false,
			errorRetryInterval: 0,
			shouldRetryOnError: true,
			errorRetryCount: 1,
			isPaused: () =>
				// check if session timestamp has expired (12h), in that case, we revalidate
				!hasTimestampExpired(timestampRef.current, SESSION_DIFF_TIMESTAMP),
			onSuccess: async (sessionData) => {
				// sync service data with cache
				if (sessionData) {
					await setSessionCacheStorage(sessionData, masterData)
					setSession({ ...sessionData, isReady: true })
					timestampRef.current = Date.now()
				}
			},
			onErrorRetry: async (_err, _key, _config, revalidate, revalidateOpts) => {
				await errorRetryHandler(
					_err,
					_key,
					_config,
					revalidate,
					revalidateOpts,
					masterData
				)
			},
		}
	)

	const handleSession = useCallback(async () => {
		const cachedSession = await getSessionCache(masterData)
		if (cachedSession) {
			setSession({ ...cachedSession, isReady: true })
			if (cachedSession.TIMESTAMP) {
				timestampRef.current = cachedSession.TIMESTAMP
			}
			return
		}
		await getOrchestratorData()
	}, [masterData, getOrchestratorData])

	const refreshSession = useCallback(async () => {
		sessionCache(masterData).clean()
		timestampRef.current = null
		await getOrchestratorData()
	}, [getOrchestratorData, masterData])

	const sessionValue = useMemo(
		() => ({ setSession, handleSession, refreshSession }),
		[handleSession, refreshSession]
	)

	useEffect(() => {
		if (!hasLoadedRef.current) {
			hasLoadedRef.current = true
			void handleSession()
		}
	}, [handleSession])

	return (
		<SessionContext.Provider value={session}>
			<SetSessionContext.Provider value={sessionValue}>
				{children}
			</SetSessionContext.Provider>
		</SessionContext.Provider>
	)
}
